import { create } from 'zustand';
import {
  type Trip,
  type POI,
  type Schedule,
  type Day,
  type TransportEvent,
  type ChangelogEvent,
  type ChangelogAction,
  type EntityType,
  type TripScheduleData,
  type LocationRef,
  type BaseLocation,
  type PermissionLevel,
  type AccommodationConfig,
} from '@poi-planner/shared';
import { useUserStore } from './userStore';
import { useUIStore } from './uiStore';
import { useLibraryStore } from './libraryStore';
import { useTripsListStore } from './tripsListStore';
import { reorderDayItemsByTime, computeTimeAtPosition } from '../utils/autoTime';
import { api } from '../services/api';
import { AUTO_SAVE_DELAY_MS } from '@poi-planner/shared';

export type SaveStatus = 'saved' | 'saving' | 'error' | 'unsaved';

// Fallback when location reference can't be resolved
const UNKNOWN_LOCATION: BaseLocation = {
  id: '',
  name: 'Unknown',
  lat: null,
  lng: null,
  notes: null,
};

/**
 * Normalizes a transport event's location ref, handling legacy format (originPoiId/destinationPoiId).
 */
function getLocationRef(event: TransportEvent, field: 'origin' | 'destination'): LocationRef | null {
  const ref = event[field];
  if (ref) return ref;

  // Fallback to legacy format
  const legacyEvent = event as any;
  const legacyId = field === 'origin' ? legacyEvent.originPoiId : legacyEvent.destinationPoiId;
  if (legacyId) return { type: 'poi', id: legacyId };

  return null;
}

interface StateSnapshot {
  schedule: Schedule;
}

// Undo/redo state
interface UndoEntry {
  before: StateSnapshot;
  after: StateSnapshot;
  summary: string;
}

const MAX_UNDO_STACK = 50;
let undoStack: UndoEntry[] = [];
let redoStack: UndoEntry[] = [];

interface TripState {
  // Data
  trip: Trip | null;
  schedule: Schedule;
  permission: PermissionLevel | 'owner' | null;
  pinnedPoiIds: Set<string>;

  // UI state
  loading: boolean;
  error: string | null;
  saveStatus: SaveStatus;

  // Changelog
  pendingChangelog: ChangelogEvent[];

  // Getters
  isReadOnly: () => boolean;

  // Actions
  loadTrip: (tripId: string) => Promise<boolean>; // Returns true if found
  unloadTrip: () => void;
  setTripData: (data: TripScheduleData) => void;

  // Pinned POI actions
  pinPoi: (poiId: string) => void;
  unpinPoi: (poiId: string) => void;
  unpinFromTrip: (poiId: string, dayId: string) => void;

  // Schedule mutations
  schedulePOI: (poiId: string, dayId: string, order: number) => void;
  unschedulePOI: (poiId: string, dayId: string) => void;
  movePOI: (poiId: string, fromDayId: string, toDayId: string, newOrder: number) => void;
  reorderPOIs: (dayId: string, oldIndex: number, newIndex: number) => void;
  addDay: () => void;
  deleteDay: (dayId: string) => void;
  reorderDays: (oldIndex: number, newIndex: number) => void;

  // Day/schedule mutations
  setSleep: (dayId: string, poiId: string | null) => void;
  createTransportEvent: (event: Omit<TransportEvent, 'id'>) => string;
  updateTransportEvent: (id: string, updates: Partial<TransportEvent>) => void;
  deleteTransportEvent: (id: string) => void;
  setStartTime: (dayId: string, poiId: string, startTime: string | null) => void;
  setTripStartDate: (newStartDate: string) => void;

  // Accommodation config mutations
  setAccommodationConfig: (poiId: string, config: Partial<AccommodationConfig>) => void;
  getAccommodationConfig: (poiId: string) => AccommodationConfig | undefined;

  // Undo/Redo actions
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;

  // Derived data helpers (delegate to libraryStore)
  getAccommodation: (id: string) => POI | undefined;
  getPOI: (id: string) => POI | undefined;
  getUnscheduledPOIs: () => POI[];
  getNightCount: (dayIndex: number) => { current: number; total: number } | null;
  resolveLocation: (ref: LocationRef) => BaseLocation;
}

// --- Auto-save state ---
let saveTimeout: ReturnType<typeof setTimeout> | null = null;
let pendingFlush: Promise<void> | null = null;

async function doSave(): Promise<void> {
  const { trip, schedule, pendingChangelog } = useTripStore.getState();
  if (!trip) return;

  useTripStore.setState({ saveStatus: 'saving' });
  try {
    await api.saveTripById(trip.id, { trip, schedule });

    if (pendingChangelog.length > 0) {
      await api.saveTripChangelog(trip.id, pendingChangelog);
      useTripStore.setState({ pendingChangelog: [] });
    }

    // Update trip summary in list
    const days = schedule.days.slice().sort((a, b) => a.date.localeCompare(b.date));
    const { permission } = useTripStore.getState();
    useTripsListStore.getState().updateTripSummary({
      id: trip.id,
      name: trip.name,
      startDate: days.length > 0 ? days[0].date : '',
      endDate: days.length > 0 ? days[days.length - 1].date : '',
      dayCount: days.length,
      createdAt: trip.createdAt,
      updatedAt: trip.updatedAt,
      isOwner: permission === 'owner',
      permission: permission || 'owner',
    });

    useTripStore.setState({ saveStatus: 'saved' });
  } catch {
    useTripStore.setState({ saveStatus: 'error' });
  }
}

function queueSave() {
  if (saveTimeout) clearTimeout(saveTimeout);
  useTripStore.setState({ saveStatus: 'unsaved' });

  saveTimeout = setTimeout(() => {
    saveTimeout = null;
    pendingFlush = doSave().finally(() => {
      pendingFlush = null;
    });
  }, AUTO_SAVE_DELAY_MS);
}

// --- Changelog helper ---
function addChangelog(
  action: ChangelogAction,
  entityType: EntityType,
  entityId: string,
  summary: string,
  before: Record<string, unknown> | null,
  after: Record<string, unknown> | null,
) {
  const userState = useUserStore.getState();
  const user = userState.getDisplayName();
  const userId = userState.getAuthUserId();

  const event: ChangelogEvent = {
    id: crypto.randomUUID(),
    timestamp: new Date().toISOString(),
    user,
    userId,
    action,
    entityType,
    entityId,
    summary,
    before,
    after,
    undoOf: null,
  };
  const pending = useTripStore.getState().pendingChangelog;
  useTripStore.setState({ pendingChangelog: [...pending, event] });
}

// --- Date helpers ---
function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + days);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// --- Undo/Redo helpers ---
let _isUndoRedo = false;
let _beforeSnapshot: StateSnapshot | null = null;

function captureSnapshot(): StateSnapshot {
  const { schedule } = useTripStore.getState();
  return JSON.parse(JSON.stringify({ schedule }));
}

function beginMutation() {
  if (!_isUndoRedo) {
    _beforeSnapshot = captureSnapshot();
  }
}

function endMutation(summary: string) {
  if (!_isUndoRedo && _beforeSnapshot) {
    const after = captureSnapshot();
    undoStack.push({ before: _beforeSnapshot, after, summary });
    if (undoStack.length > MAX_UNDO_STACK) {
      undoStack.shift();
    }
    redoStack = [];
    _beforeSnapshot = null;
  }
}

// Default empty schedule
const emptySchedule: Schedule = { days: [], transportEvents: [], accommodationConfigs: {} };

export const useTripStore = create<TripState>((set, get) => ({
  trip: null,
  schedule: emptySchedule,
  permission: null,
  pinnedPoiIds: new Set<string>(),
  loading: false,
  error: null,
  saveStatus: 'saved' as SaveStatus,
  pendingChangelog: [],

  isReadOnly: () => {
    const { permission } = get();
    return permission === 'viewer';
  },

  loadTrip: async (tripId: string) => {
    set({ loading: true, error: null });
    try {
      const [data, permission, pinnedIds] = await Promise.all([
        api.loadTripById(tripId),
        api.getTripPermission(tripId),
        api.getPinnedPoiIds(tripId).catch(() => [] as string[]),
      ]);

      if (!data) {
        set({ trip: null, permission: null, loading: false });
        return false;
      }

      // Ensure accommodationConfigs exists (for backward compatibility)
      const schedule = {
        ...data.schedule,
        accommodationConfigs: data.schedule.accommodationConfigs ?? {},
      };

      set({
        trip: data.trip,
        schedule,
        permission,
        pinnedPoiIds: new Set(pinnedIds),
        loading: false,
        pendingChangelog: [],
      });

      // Reset undo/redo stacks when loading a new trip
      undoStack = [];
      redoStack = [];
      return true;
    } catch (err) {
      set({ error: (err as Error).message, loading: false });
      return false;
    }
  },

  unloadTrip: () => {
    // Cancel any pending saves
    if (saveTimeout) {
      clearTimeout(saveTimeout);
      saveTimeout = null;
    }
    set({
      trip: null,
      schedule: emptySchedule,
      permission: null,
      pinnedPoiIds: new Set<string>(),
      pendingChangelog: [],
      saveStatus: 'saved',
    });
    undoStack = [];
    redoStack = [];
  },

  setTripData: (data: TripScheduleData) => {
    // Ensure accommodationConfigs exists
    const schedule = {
      ...data.schedule,
      accommodationConfigs: data.schedule.accommodationConfigs ?? {},
    };
    set({
      trip: data.trip,
      schedule,
    });
  },

  // --- Schedule mutations ---

  schedulePOI: (poiId: string, dayId: string, order: number) => {
    const { schedule, getAccommodationConfig } = get();
    const { pois } = useLibraryStore.getState();
    const poi = pois.find((p) => p.id === poiId);
    const dayIndex = schedule.days.findIndex((d) => d.id === dayId);
    const day = schedule.days[dayIndex];
    if (!day || !poi) return;

    // Don't schedule same POI twice on same day
    if (day.scheduledPOIs.some((sp) => sp.poiId === poiId)) return;

    beginMutation();

    // Get wake accommodation (previous day's sleep)
    const prevDay = dayIndex > 0 ? schedule.days[dayIndex - 1] : null;
    const wakeAccomPoi = prevDay?.sleep
      ? pois.find((p) => p.id === prevDay.sleep?.poiId)
      : undefined;
    const wakeAccomConfig = prevDay?.sleep
      ? getAccommodationConfig(prevDay.sleep.poiId)
      : undefined;

    // Create a pseudo-accommodation for auto-time calculation
    const wakeAccommodation = wakeAccomPoi && wakeAccomConfig
      ? { ...wakeAccomPoi, checkOutTime: wakeAccomConfig.checkOutTime ?? null }
      : undefined;

    const startTime = computeTimeAtPosition(day, schedule.transportEvents, pois, order, wakeAccommodation as any);

    const newDays = schedule.days.map((d) => {
      if (d.id !== dayId) return d;
      const sorted = [...d.scheduledPOIs].sort((a, b) => a.order - b.order);
      sorted.splice(order, 0, { poiId, startTime, order: 0 });
      return { ...d, scheduledPOIs: sorted.map((sp, i) => ({ ...sp, order: i })) };
    });

    set({ schedule: { ...schedule, days: newDays } });
    addChangelog('schedule', 'poi', poiId, `Scheduled "${poi.name}"`, null, { dayId, order, startTime });
    queueSave();
    endMutation(`Scheduled "${poi.name}"`);

    // Auto-pin if not already pinned
    const { pinnedPoiIds, trip: currentTrip } = get();
    if (currentTrip && !pinnedPoiIds.has(poiId)) {
      const newPinned = new Set(pinnedPoiIds);
      newPinned.add(poiId);
      set({ pinnedPoiIds: newPinned });
      api.pinPoisToTrip(currentTrip.id, [poiId]).catch(() => {
        // Rollback on error
        set((state) => {
          const rolled = new Set(state.pinnedPoiIds);
          rolled.delete(poiId);
          return { pinnedPoiIds: rolled };
        });
      });
    }
  },

  unschedulePOI: (poiId: string, dayId: string) => {
    const { schedule } = get();
    const { pois } = useLibraryStore.getState();
    const poi = pois.find((p) => p.id === poiId);
    const day = schedule.days.find((d) => d.id === dayId);
    if (!day) return;

    const beforeSp = day.scheduledPOIs.find((sp) => sp.poiId === poiId);
    if (!beforeSp) return;

    beginMutation();
    const newDays = schedule.days.map((d) => {
      if (d.id !== dayId) return d;
      const filtered = d.scheduledPOIs.filter((sp) => sp.poiId !== poiId);
      return { ...d, scheduledPOIs: filtered.map((sp, i) => ({ ...sp, order: i })) };
    });

    set({ schedule: { ...schedule, days: newDays } });
    addChangelog('unschedule', 'poi', poiId, `Unscheduled "${poi?.name ?? poiId}"`, { dayId }, null);
    queueSave();
    endMutation(`Unscheduled "${poi?.name ?? poiId}"`);

    // Clear target if this POI was targeted
    const targetPoiId = useUIStore.getState().targetPoiId;
    if (targetPoiId === poiId) {
      useUIStore.getState().setTargetPoiId(null);
    }
  },

  movePOI: (poiId: string, fromDayId: string, toDayId: string, newOrder: number) => {
    const { schedule, getAccommodationConfig } = get();
    const { pois } = useLibraryStore.getState();
    const poi = pois.find((p) => p.id === poiId);
    const fromDay = schedule.days.find((d) => d.id === fromDayId);
    const toDayIndex = schedule.days.findIndex((d) => d.id === toDayId);
    const toDay = schedule.days[toDayIndex];
    if (!fromDay || !toDay) return;

    const sp = fromDay.scheduledPOIs.find((s) => s.poiId === poiId);
    if (!sp) return;

    // Don't allow duplicate on same day
    if (toDay.scheduledPOIs.some((s) => s.poiId === poiId)) return;

    beginMutation();

    const prevDay = toDayIndex > 0 ? schedule.days[toDayIndex - 1] : null;
    const wakeAccomPoi = prevDay?.sleep
      ? pois.find((p) => p.id === prevDay.sleep?.poiId)
      : undefined;
    const wakeAccomConfig = prevDay?.sleep
      ? getAccommodationConfig(prevDay.sleep.poiId)
      : undefined;

    const wakeAccommodation = wakeAccomPoi && wakeAccomConfig
      ? { ...wakeAccomPoi, checkOutTime: wakeAccomConfig.checkOutTime ?? null }
      : undefined;

    const startTime = computeTimeAtPosition(toDay, schedule.transportEvents, pois, newOrder, wakeAccommodation as any);

    const newDays = schedule.days.map((d) => {
      if (d.id === fromDayId) {
        const filtered = d.scheduledPOIs.filter((s) => s.poiId !== poiId);
        return { ...d, scheduledPOIs: filtered.map((s, i) => ({ ...s, order: i })) };
      }
      if (d.id === toDayId) {
        const sorted = [...d.scheduledPOIs].sort((a, b) => a.order - b.order);
        sorted.splice(newOrder, 0, { ...sp, startTime, order: 0 });
        return { ...d, scheduledPOIs: sorted.map((s, i) => ({ ...s, order: i })) };
      }
      return d;
    });

    set({ schedule: { ...schedule, days: newDays } });
    addChangelog('schedule', 'poi', poiId, `Moved "${poi?.name ?? poiId}" to another day`, { fromDayId }, { toDayId, order: newOrder, startTime });
    queueSave();
    endMutation(`Moved "${poi?.name ?? poiId}"`);
  },

  reorderPOIs: (dayId: string, oldIndex: number, newIndex: number) => {
    const { schedule, getAccommodationConfig } = get();
    const { pois } = useLibraryStore.getState();
    const dayIndex = schedule.days.findIndex((d) => d.id === dayId);
    const day = schedule.days[dayIndex];
    if (!day) return;

    beginMutation();
    const sorted = [...day.scheduledPOIs].sort((a, b) => a.order - b.order);
    const [moved] = sorted.splice(oldIndex, 1);
    sorted.splice(newIndex, 0, moved);

    const prevDay = dayIndex > 0 ? schedule.days[dayIndex - 1] : null;
    const wakeAccomPoi = prevDay?.sleep
      ? pois.find((p) => p.id === prevDay.sleep?.poiId)
      : undefined;
    const wakeAccomConfig = prevDay?.sleep
      ? getAccommodationConfig(prevDay.sleep.poiId)
      : undefined;

    const wakeAccommodation = wakeAccomPoi && wakeAccomConfig
      ? { ...wakeAccomPoi, checkOutTime: wakeAccomConfig.checkOutTime ?? null }
      : undefined;

    const tempDay = {
      ...day,
      scheduledPOIs: sorted.filter(sp => sp.poiId !== moved.poiId).map((sp, i) => ({ ...sp, order: i })),
    };
    const startTime = computeTimeAtPosition(tempDay, schedule.transportEvents, pois, newIndex, wakeAccommodation as any);

    const newDays = schedule.days.map((d) => {
      if (d.id !== dayId) return d;
      return {
        ...d,
        scheduledPOIs: sorted.map((sp, i) => ({
          ...sp,
          order: i,
          startTime: sp.poiId === moved.poiId ? startTime : sp.startTime,
        })),
      };
    });

    set({ schedule: { ...schedule, days: newDays } });
    addChangelog('reorder', 'day', dayId, `Reordered POIs`, { oldIndex }, { newIndex });
    queueSave();
    endMutation('Reordered POIs');
  },

  addDay: () => {
    const { schedule, trip } = get();
    if (!trip) return;
    const days = schedule.days;
    const lastDay = days[days.length - 1];

    beginMutation();
    const newDate = lastDay ? addDays(lastDay.date, 1) : new Date().toISOString().split('T')[0];
    const timezone = lastDay?.timezone ?? 'UTC';
    const sleep = lastDay?.sleep ? { ...lastDay.sleep } : null;

    const newDay: Day = {
      id: crypto.randomUUID(),
      date: newDate,
      timezone,
      wakeUpTime: '08:00',
      scheduledPOIs: [],
      sleep,
    };

    const newDays = [...days, newDay];
    set({ schedule: { ...schedule, days: newDays } });
    addChangelog('create', 'day', newDay.id, `Added Day ${newDays.length}`, null, { date: newDate });
    queueSave();
    endMutation(`Added Day ${newDays.length}`);
  },

  deleteDay: (dayId: string) => {
    const { schedule } = get();
    const dayIndex = schedule.days.findIndex((d) => d.id === dayId);
    if (dayIndex === -1) return;

    beginMutation();
    const deletedDay = schedule.days[dayIndex];
    const newDays = schedule.days.filter((d) => d.id !== dayId);

    for (let i = dayIndex; i < newDays.length; i++) {
      if (i === 0) continue;
      const prevDate = newDays[i - 1].date;
      newDays[i] = { ...newDays[i], date: addDays(prevDate, 1) };
    }

    set({ schedule: { ...schedule, days: newDays } });
    addChangelog('delete', 'day', dayId, `Deleted day (${deletedDay.date})`, { date: deletedDay.date, scheduledPOIs: deletedDay.scheduledPOIs }, null);
    queueSave();
    endMutation(`Deleted day (${deletedDay.date})`);
  },

  reorderDays: (oldIndex: number, newIndex: number) => {
    const { schedule } = get();
    if (oldIndex === newIndex) return;
    if (oldIndex < 0 || oldIndex >= schedule.days.length) return;
    if (newIndex < 0 || newIndex >= schedule.days.length) return;

    beginMutation();

    const newDays = [...schedule.days];
    const [movedDay] = newDays.splice(oldIndex, 1);
    newDays.splice(newIndex, 0, movedDay);

    const originalDates = schedule.days.map((d) => d.date);
    const reorderedDays = newDays.map((d, i) => ({
      ...d,
      date: originalDates[i],
    }));

    set({ schedule: { ...schedule, days: reorderedDays } });
    addChangelog('reorder', 'day', movedDay.id, `Reordered days (Day ${oldIndex + 1} → Day ${newIndex + 1})`, { oldIndex }, { newIndex });
    queueSave();
    endMutation(`Reordered days`);
  },

  // --- Day/schedule mutations ---

  setSleep: (dayId: string, poiId: string | null) => {
    const { schedule } = get();
    const day = schedule.days.find((d) => d.id === dayId);
    if (!day) return;

    beginMutation();
    const beforePoiId = day.sleep?.poiId ?? null;
    const newDays = schedule.days.map((d) => {
      if (d.id !== dayId) return d;
      return { ...d, sleep: poiId ? { poiId } : null };
    });

    // Auto-create accommodation config if not exists
    let newConfigs = { ...schedule.accommodationConfigs };
    if (poiId && !newConfigs[poiId]) {
      // Find next available color (count existing configs)
      const usedColors = Object.values(newConfigs).map((c) => c.color);
      let nextColor = 0;
      for (let i = 0; i < 8; i++) {
        if (!usedColors.includes(i)) {
          nextColor = i;
          break;
        }
      }
      newConfigs[poiId] = {
        color: nextColor,
        checkInTime: '15:00',
        checkOutTime: '11:00',
      };
    }

    set({ schedule: { ...schedule, days: newDays, accommodationConfigs: newConfigs } });
    addChangelog('sleep_change', 'day', dayId, `Changed sleep assignment`, { poiId: beforePoiId }, { poiId });
    queueSave();
    endMutation('Changed sleep assignment');
  },

  // --- Accommodation config mutations ---

  setAccommodationConfig: (poiId: string, config: Partial<AccommodationConfig>) => {
    const { schedule } = get();
    const existing = schedule.accommodationConfigs[poiId] ?? { color: 0 };
    const updated = { ...existing, ...config };
    const newConfigs = { ...schedule.accommodationConfigs, [poiId]: updated };

    set({ schedule: { ...schedule, accommodationConfigs: newConfigs } });
    queueSave();
  },

  getAccommodationConfig: (poiId: string) => {
    return get().schedule.accommodationConfigs[poiId];
  },

  createTransportEvent: (eventData: Omit<TransportEvent, 'id'>) => {
    beginMutation();
    const id = crypto.randomUUID();
    const event: TransportEvent = { ...eventData, id };
    const { schedule, resolveLocation } = get();
    const { pois } = useLibraryStore.getState();

    const originName = resolveLocation(event.origin).name;
    const destName = resolveLocation(event.destination).name;

    let newEvents = [...schedule.transportEvents, event];
    let newDays = schedule.days;

    const day = schedule.days.find((d) => d.id === event.dayId);
    if (day) {
      const reordered = reorderDayItemsByTime(day, newEvents, pois);
      newDays = schedule.days.map((d) =>
        d.id === day.id ? { ...d, scheduledPOIs: reordered.scheduledPOIs } : d
      );
      newEvents = reordered.transportEvents;
    }

    set({ schedule: { ...schedule, days: newDays, transportEvents: newEvents } });
    addChangelog('create', 'transport', id, `Created ${event.mode} ${originName} → ${destName}`, null, { mode: event.mode, dayId: event.dayId });
    queueSave();
    endMutation(`Created ${event.mode} ${originName} → ${destName}`);
    return id;
  },

  updateTransportEvent: (id: string, updates: Partial<TransportEvent>) => {
    const { schedule, resolveLocation } = get();
    const { pois } = useLibraryStore.getState();
    const idx = schedule.transportEvents.findIndex((te) => te.id === id);
    if (idx === -1) return;
    beginMutation();
    const before = schedule.transportEvents[idx];
    const updated = { ...before, ...updates, id };
    let newEvents = [...schedule.transportEvents];
    newEvents[idx] = updated;

    let newDays = schedule.days;

    if (updates.departureTime !== undefined && updates.departureTime !== before.departureTime) {
      const day = schedule.days.find((d) => d.id === updated.dayId);
      if (day) {
        const reordered = reorderDayItemsByTime(day, newEvents, pois);
        newDays = schedule.days.map((d) =>
          d.id === day.id ? { ...d, scheduledPOIs: reordered.scheduledPOIs } : d
        );
        newEvents = reordered.transportEvents;
      }
    }

    const originRef = getLocationRef(updated, 'origin');
    const destRef = getLocationRef(updated, 'destination');
    const originName = originRef ? resolveLocation(originRef).name : 'Unknown';
    const destName = destRef ? resolveLocation(destRef).name : 'Unknown';

    set({ schedule: { ...schedule, days: newDays, transportEvents: newEvents } });
    addChangelog('update', 'transport', id, `Updated ${updated.mode} ${originName} → ${destName}`, { mode: before.mode }, { mode: updated.mode });
    queueSave();
    endMutation(`Updated ${updated.mode} transport`);
  },

  deleteTransportEvent: (id: string) => {
    const { schedule, resolveLocation } = get();
    const event = schedule.transportEvents.find((te) => te.id === id);
    if (!event) return;

    const originRef = getLocationRef(event, 'origin');
    const destRef = getLocationRef(event, 'destination');
    const originName = originRef ? resolveLocation(originRef).name : 'Unknown';
    const destName = destRef ? resolveLocation(destRef).name : 'Unknown';

    beginMutation();
    const newEvents = schedule.transportEvents.filter((te) => te.id !== id);

    set({ schedule: { ...schedule, transportEvents: newEvents } });
    addChangelog('delete', 'transport', id, `Deleted ${event.mode} ${originName} → ${destName}`, { mode: event.mode }, null);
    queueSave();
    endMutation(`Deleted ${event.mode} transport`);
  },

  setStartTime: (dayId: string, poiId: string, startTime: string | null) => {
    const { schedule } = get();
    const { pois } = useLibraryStore.getState();
    const day = schedule.days.find((d) => d.id === dayId);
    if (!day) return;

    const sp = day.scheduledPOIs.find((s) => s.poiId === poiId);
    if (!sp) return;

    beginMutation();

    let updatedDay = {
      ...day,
      scheduledPOIs: day.scheduledPOIs.map((s) =>
        s.poiId === poiId ? { ...s, startTime } : s
      ),
    };

    let newTransportEvents = schedule.transportEvents;

    if (startTime !== null) {
      const reordered = reorderDayItemsByTime(updatedDay, schedule.transportEvents, pois);
      updatedDay = { ...updatedDay, scheduledPOIs: reordered.scheduledPOIs };
      newTransportEvents = reordered.transportEvents;
    }

    const newDays = schedule.days.map((d) =>
      d.id === dayId ? updatedDay : d
    );

    set({ schedule: { ...schedule, days: newDays, transportEvents: newTransportEvents } });
    addChangelog('update', 'poi', poiId, `Set start time to ${startTime ?? 'auto'}`, { startTime: sp.startTime }, { startTime });
    queueSave();
    endMutation(`Set start time to ${startTime ?? 'auto'}`);
  },

  setTripStartDate: (newStartDate: string) => {
    const { schedule } = get();
    if (schedule.days.length === 0) return;

    const oldStartDate = schedule.days[0].date;
    if (oldStartDate === newStartDate) return;

    beginMutation();

    const newDays = schedule.days.map((d, i) => ({
      ...d,
      date: addDays(newStartDate, i),
    }));

    set({ schedule: { ...schedule, days: newDays } });
    addChangelog('update', 'trip', 'trip', `Changed start date from ${oldStartDate} to ${newStartDate}`, { startDate: oldStartDate }, { startDate: newStartDate });
    queueSave();
    endMutation(`Changed start date to ${newStartDate}`);
  },

  // --- Pinned POI actions ---

  pinPoi: (poiId: string) => {
    const { trip, pinnedPoiIds } = get();
    if (!trip || pinnedPoiIds.has(poiId)) return;

    // Optimistic update
    const newPinned = new Set(pinnedPoiIds);
    newPinned.add(poiId);
    set({ pinnedPoiIds: newPinned });

    api.pinPoisToTrip(trip.id, [poiId]).catch(() => {
      // Rollback on error
      set((state) => {
        const rolled = new Set(state.pinnedPoiIds);
        rolled.delete(poiId);
        return { pinnedPoiIds: rolled };
      });
    });
  },

  unpinPoi: (poiId: string) => {
    const { trip, pinnedPoiIds } = get();
    if (!trip || !pinnedPoiIds.has(poiId)) return;

    // Optimistic update
    const newPinned = new Set(pinnedPoiIds);
    newPinned.delete(poiId);
    set({ pinnedPoiIds: newPinned });

    api.unpinPoisFromTrip(trip.id, [poiId]).catch(() => {
      // Rollback on error
      set((state) => {
        const rolled = new Set(state.pinnedPoiIds);
        rolled.add(poiId);
        return { pinnedPoiIds: rolled };
      });
    });
  },

  unpinFromTrip: (poiId: string, dayId: string) => {
    // Unschedule from day
    const { schedule } = get();
    const day = schedule.days.find((d) => d.id === dayId);
    if (!day) return;

    const { pois } = useLibraryStore.getState();
    const poi = pois.find((p) => p.id === poiId);
    const beforeSp = day.scheduledPOIs.find((sp) => sp.poiId === poiId);
    if (!beforeSp) return;

    beginMutation();
    const newDays = schedule.days.map((d) => {
      if (d.id !== dayId) return d;
      const filtered = d.scheduledPOIs.filter((sp) => sp.poiId !== poiId);
      return { ...d, scheduledPOIs: filtered.map((sp, i) => ({ ...sp, order: i })) };
    });

    set({ schedule: { ...schedule, days: newDays } });
    addChangelog('unschedule', 'poi', poiId, `Unscheduled and unpinned "${poi?.name ?? poiId}"`, { dayId }, null);
    queueSave();
    endMutation(`Unscheduled and unpinned "${poi?.name ?? poiId}"`);

    // Clear target if this POI was targeted
    const targetPoiId = useUIStore.getState().targetPoiId;
    if (targetPoiId === poiId) {
      useUIStore.getState().setTargetPoiId(null);
    }

    // Also unpin
    const { trip, pinnedPoiIds } = get();
    if (trip && pinnedPoiIds.has(poiId)) {
      const newPinned = new Set(pinnedPoiIds);
      newPinned.delete(poiId);
      set({ pinnedPoiIds: newPinned });

      api.unpinPoisFromTrip(trip.id, [poiId]).catch(() => {
        // Rollback pin state on error
        set((state) => {
          const rolled = new Set(state.pinnedPoiIds);
          rolled.add(poiId);
          return { pinnedPoiIds: rolled };
        });
      });
    }
  },

  // --- Undo/Redo actions ---

  undo: () => {
    const entry = undoStack.pop();
    if (!entry) return;

    redoStack.push(entry);

    _isUndoRedo = true;
    set({ schedule: entry.before.schedule });
    _isUndoRedo = false;

    const user = useUserStore.getState().displayName || 'Unknown';
    const event: ChangelogEvent = {
      id: crypto.randomUUID(),
      timestamp: new Date().toISOString(),
      user,
      action: 'undo',
      entityType: 'trip',
      entityId: '',
      summary: `Undo: ${entry.summary}`,
      before: null,
      after: null,
      undoOf: null,
    };
    const pending = get().pendingChangelog;
    set({ pendingChangelog: [...pending, event] });
    queueSave();
  },

  redo: () => {
    const entry = redoStack.pop();
    if (!entry) return;

    undoStack.push(entry);

    _isUndoRedo = true;
    set({ schedule: entry.after.schedule });
    _isUndoRedo = false;

    const user = useUserStore.getState().displayName || 'Unknown';
    const event: ChangelogEvent = {
      id: crypto.randomUUID(),
      timestamp: new Date().toISOString(),
      user,
      action: 'redo',
      entityType: 'trip',
      entityId: '',
      summary: `Redo: ${entry.summary}`,
      before: null,
      after: null,
      undoOf: null,
    };
    const pending = get().pendingChangelog;
    set({ pendingChangelog: [...pending, event] });
    queueSave();
  },

  canUndo: () => undoStack.length > 0,
  canRedo: () => redoStack.length > 0,

  // --- Derived data helpers ---

  getAccommodation: (id: string) => {
    return useLibraryStore.getState().getAccommodation(id);
  },

  getPOI: (id: string) => {
    return useLibraryStore.getState().getPOI(id);
  },

  getUnscheduledPOIs: () => {
    const { schedule } = get();
    const { pois } = useLibraryStore.getState();
    const scheduledIds = new Set<string>();
    for (const day of schedule.days) {
      for (const sp of day.scheduledPOIs) {
        scheduledIds.add(sp.poiId);
      }
    }
    return pois.filter((p) => !scheduledIds.has(p.id));
  },

  getNightCount: (dayIndex: number) => {
    const { schedule } = get();
    const days = schedule.days;
    const day = days[dayIndex];
    if (!day?.sleep) return null;

    const poiId = day.sleep.poiId;

    let start = dayIndex;
    while (start > 0 && days[start - 1]?.sleep?.poiId === poiId) {
      start--;
    }
    let end = dayIndex;
    while (end < days.length - 1 && days[end + 1]?.sleep?.poiId === poiId) {
      end++;
    }

    const current = dayIndex - start + 1;
    const total = end - start + 1;
    return { current, total };
  },

  resolveLocation: (ref: LocationRef): BaseLocation => {
    const store = useLibraryStore.getState();
    // All locations are now POIs (accommodations have "accommodation" tag)
    const location = store.getPOI(ref.id);
    return location ?? UNKNOWN_LOCATION;
  },
}));

// Export flush function for external use
export async function flushTripSave(): Promise<void> {
  if (saveTimeout) {
    clearTimeout(saveTimeout);
    saveTimeout = null;
    await doSave();
  } else if (pendingFlush) {
    await pendingFlush;
  }
}
